import { useEffect, useState, useCallback, useMemo } from "react";
import api from "../api/api";
import {
  Paper, Typography, Button, IconButton, Grid, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Tooltip
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import VersionForm from "../components/VersionForm";
import ConfirmDialog from "../components/ConfirmDialog";

export default function Versiones() {
  const [cursos, setCursos] = useState([]);
  const [idCurso, setIdCurso] = useState("");
  const [versiones, setVersiones] = useState([]);

  const [openForm, setOpenForm] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // -- load courses
  const cargarCursos = useCallback(async () => {
    try {
      const res = await api.get("/cursos");
      setCursos(res.data);
      if (!idCurso && res.data.length) {
        setIdCurso(res.data[0].id_curso.toString());
      }
    } catch (e) {
      alert("You do not have permission to view courses.");
    }
  }, [idCurso]);

  // -- load versions
  const cargarVersiones = useCallback(async () => {
    try {
      const params = {};
      if (idCurso) params.id_curso = idCurso;
      const res = await api.get("/versiones", { params });
      setVersiones(res.data);
    } catch (e) {
      alert("You do not have permission to view versions.");
    }
  }, [idCurso]);

  useEffect(() => { cargarCursos(); }, [cargarCursos]);
  useEffect(() => { if (idCurso) cargarVersiones(); }, [idCurso, cargarVersiones]);

  const cursoMap = useMemo(() => {
    const map = new Map();
    cursos.forEach((c) => map.set(c.id_curso, c.nombre_curso));
    return map;
  }, [cursos]);

  const publicar = async (v) => {
    try {
      await api.post(`/versiones/${v.id_version}/publicar`);
      cargarVersiones();
    } catch (e) {
      alert("Could not publish the version.");
    }
  };

  const eliminar = async () => {
    try {
      await api.delete(`/versiones/${deleteId}`);
      setConfirmOpen(false);
      cargarVersiones();
    } catch (e) {
      alert("Cannot delete: version is referenced.");
    }
  };

  const formatFecha = (iso) => {
    try { return new Date(iso).toLocaleDateString(); }
    catch { return iso; }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Course Versions
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <TextField select fullWidth label="Course"
            value={idCurso} onChange={(e) => setIdCurso(e.target.value)}
          >
            {cursos.map(c => (
              <MenuItem key={c.id_curso} value={c.id_curso}>
                {c.nombre_curso} ({c.tipo})
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs />

        <Grid item>
          <Button variant="contained" onClick={() => {
            setSelectedVersion(null);
            setOpenForm(true);
          }}>
            New version
          </Button>
        </Grid>
      </Grid>

      <Table>
        <TableHead>
          <TableRow sx={{ background: "#000" }}>
            <TableCell sx={{ color: "white" }}>Course</TableCell>
            <TableCell sx={{ color: "white" }}>Version</TableCell>
            <TableCell sx={{ color: "white" }}>Published</TableCell>
            <TableCell sx={{ color: "white" }}>Validity</TableCell>
            <TableCell sx={{ color: "white" }}>Change description</TableCell>
            <TableCell sx={{ color: "white" }}>Status</TableCell>
            <TableCell sx={{ color: "white" }}>Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {versiones.map(v => (
            <TableRow key={v.id_version}>
              <TableCell>{cursoMap.get(v.id_curso)}</TableCell>
              <TableCell>{v.numero_version}</TableCell>
              <TableCell>{formatFecha(v.fecha_publicacion)}</TableCell>
              <TableCell>
                {v.vigencia_meses
                  ? `${v.vigencia_meses} months`
                  : <span style={{ color: "#888", fontStyle: "italic" }}>Until new version</span>}
              </TableCell>
              <TableCell sx={{ maxWidth: 280 }}>
                {v.descripcion_cambios ? (
                  <Tooltip title={v.descripcion_cambios} placement="top-start">
                    <span style={{ cursor: "default", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {v.descripcion_cambios}
                    </span>
                  </Tooltip>
                ) : (
                  <span style={{ color: "#999" }}>—</span>
                )}
              </TableCell>
              <TableCell>
                {v.vigente
                  ? <Chip label="Current" color="success" />
                  : <Chip label="Not current" />}
              </TableCell>

              <TableCell>
                <IconButton color="primary" onClick={() => {
                  setSelectedVersion(v);
                  setOpenForm(true);
                }}>
                  <EditIcon />
                </IconButton>

                {!v.vigente && (
                  <IconButton color="secondary" onClick={() => publicar(v)}>
                    <RocketLaunchIcon />
                  </IconButton>
                )}

                <IconButton color="error" onClick={() => {
                  setDeleteId(v.id_version);
                  setConfirmOpen(true);
                }}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <VersionForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        version={selectedVersion}
        cursos={cursos}
        idCursoDefault={idCurso}
        onSaved={cargarVersiones}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Delete version?"
        onConfirm={eliminar}
      />
    </Paper>
  );
}